"use client";
import { useRef, useState, useEffect } from "react";
import { Container, Row, Col } from "reactstrap";
import { Link, NavLink } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import "../../styles/header.css";
import logoImg from "../../assets/all-images/logo.png";

const Header = () => {
  const menuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = useTranslation();

  const navLinks = [
    { path: "/home", icon: "ri-home-4-line", key: "nav.home" },
    { path: "/about", icon: "ri-information-line", key: "nav.about" },
    { path: "/services", icon: "ri-service-line", key: "nav.services" },
    { path: "/equipment", icon: "ri-truck-line", key: "nav.equipment" },
    { path: "/projects", icon: "ri-building-line", key: "nav.projects" },
    { path: "/ourteam", icon: "ri-group-line", key: "nav.ourteam" },
    { path: "/developercrew", icon: "ri-code-line", key: "nav.developercrew" },
    { path: "/contact", icon: "ri-phone-line", key: "nav.contact" },
    { path: "/blogs", icon: "ri-article-line", key: "nav.blog" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    menuRef.current.classList.toggle("menu__active");
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className={`header ${scrolled ? "scrolled" : ""}`}>
      {/* Mobile Top Bar (visible only on small screens) */}
      <div className="mobile-top-bar d-lg-none">
        <div className="container">
          <div className="mobile-header-container">
            <button className="mobile-menu-btn" onClick={toggleMenu}>
              <i
                className={isMenuOpen ? "ri-close-line" : "ri-menu-line"}
                style={{ color: "white" }}
              ></i>
            </button>

            <Link to="/home" className="mobile-logo-container">
              <div className="circular-logo-wrapper" style={{ width: "150px" ,height: "60px"}}>
                <img src={logoImg} alt="logo" className="circular-logo" />
              </div>
              
            </Link>

            <div className="mobile-actions">
              <button
                onClick={toggleLanguage}
                className="mobile-action-btn language-btn"
                aria-label={
                  language === "en" ? "Switch to Arabic" : "Switch to English"
                }
              >
                <i
                  className={
                    language === "en" ? "ri-global-line" : "ri-translate-2"
                  }
                ></i>
                <span className="language-text">
                  {language === "en" ? "AR" : "EN"}
                </span>
              </button>
              <button
                onClick={() => {
                  window.location.href = "/login";
                }}
                className="mobile-action-btn login-btn"
                aria-label="Login"
              >
                <i className="ri-user-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Header Top */}
      <div className="header__top d-none d-lg-block">
        <Container>
          <Row>
            <Col lg="6" md="6" sm="6">
              <div className="header__top__left">
                <div className="header-contact-info">
                  <i className="ri-phone-fill"></i>
                  <span>+966538077541</span>
                </div>
                <div className="header-contact-info">
                  <i className="ri-mail-fill"></i>
                  <span>Ksa@unitedlimited.net </span>
                </div>
              </div>
            </Col>

            <Col lg="6" md="6" sm="6">
              <div className="header__top__right">
                <div className="social-links">
                  <a href="#">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="#">
                    <i className="ri-twitter-fill"></i>
                  </a>
                  <a href="#">
                    <i className="ri-linkedin-fill"></i>
                  </a>
                  <a href="#">
                    <i className="ri-instagram-line"></i>
                  </a>
                </div>

                <div className="header-actions">
                  <button
                    onClick={toggleLanguage}
                    className="language-switcher"
                  >
                    {language === "en" ? "العربية" : "English"}
                    <i
                      className={
                        language === "en" ? "ri-global-line" : "ri-translate-2"
                      }
                    ></i>
                  </button>

                  <button
                    onClick={() => {
                      window.location.href = "/login";
                    }}
                    className="login-btn"
                  >
                    <i className="ri-user-line"></i>
                    {t("header.login")}
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Header Middle */}
      <div className="header__middle d-none d-lg-block">
        <Container>
          <Row className="align-items-center">
            <Col lg="4" md="3" sm="4">
              <div className="logo">
                <Link to="/home" className="logo-link">
                  <img src={logoImg} alt="logo" className="logo-icon" />
                  {/* <div className="logo-text">
                    <span className="logo-main">{t("header.title")}</span>
                    <span className="logo-sub">{t("header.subtitle")}</span>
                  </div> */}
                </Link>
              </div>
            </Col>

            <Col lg="4" md="6" sm="4" className="d-none d-md-block">
              <div className="header-info-container">
                <div className="header-info-item">
                  <div className="header-info-icon">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div className="header-info-content">
                    <h4>{t("header.location")}</h4>
                    <h6>Riyadh, KSA</h6>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg="4" md="3" sm="4" className="text-end">
              <Link to="/contact" className="header-cta">
                <div className="cta-icon">
                  <i className="ri-phone-fill"></i>
                </div>
                <div className="cta-text">
                  <span>{t("header.emergency")}</span>
                  <strong>{t("header.contact")}</strong>
                </div>
              </Link>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Main Navigation */}
      <div className={`main__navbar ${scrolled ? "scrolled" : ""}`}>
        <Container>
          <div className="navigation__wrapper">
            <span className="mobile__menu d-lg-none">
              <i
                className={isMenuOpen ? "ri-close-line" : "ri-menu-line"}
                onClick={toggleMenu}
              ></i>
            </span>

            <div className="navigation" ref={menuRef} onClick={toggleMenu}>
              <div className="menu">
                {navLinks.map((item) => (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      isActive ? "nav__active nav__item" : "nav__item"
                    }
                    key={item.key}
                  >
                    <i className={`${item.icon} nav-icon`}></i>
                    {t(item.key)}
                    <div className="nav-underline"></div>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* <div className="nav__right d-none d-lg-block">
              <div className="search__box">
                <input type="text" placeholder={t("header.search")} />
                <i className="ri-search-line"></i>
              </div>
            </div> */}
          </div>
        </Container>
      </div>
    </header>
  );
};

export default Header;
